import sanitizeHtml from "sanitize-html";
import Title from "../../Global/Title/Title";
import { HEADING_LEVELS } from "../../Global/GlobalConstants";
import VideoIFrame from "../../Common/VideoIFrame/VideoIFrame";
import "./HotSpots.scss";

/**
 * Renders the content tile for a product hot spot
 */
export const ProductHotSpotContent = ({
  productData,
  style,
  onClose,
  onMouseEnter,
  onMouseLeave,
  onKeyUp,
  focusOnContent,
  addYourBrandLabel,
  newProductLabel,
}) => {
  // Defensive check for missing data
  if (!productData) {
    console.error('ProductHotSpotContent: productData is undefined');
    return null;
  }

  const {
    catalogImage,
    catalogImageAlternateText,
    childProductName,
    productCode,
    ctaLink,
    isProPlanet,
    isAddBrand,
    isNew,
  } = productData;

  const hasMedia = Boolean(catalogImage);
  const mainClassName = hasMedia ? "__main __main--withmedia" : "__main";
  const contentStyle = { ...style, width: "330px", height: "auto" };

  return (
    <div
      className="__hot-spot-content __hot-spot-content--active"
      tabIndex="0"
      id="hot-spot-content"
      style={contentStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyUp={onKeyUp}
    >
      <button className="__close darticon-close" onClick={onClose}></button>
      <div className="__content __content">
        <div className="__tags __tags--top">
          {isNew && <div className="__tag __tag--new">{newProductLabel}</div>}
          {isProPlanet && (
            <div className="__tag __tag--planet" aria-label="Pro Planet">
              <img
                width="50"
                src="https://dart.widen.net/content/vsjmstuwtr/svg/ProPlanet-Leaf-CheckmarkwStroke-Green.svg?u=hsm95h"
                alt="Pro Planet"
              />
            </div>
          )}
        </div>
        <div className={mainClassName}>
          {catalogImage && (
            <div className="__media">
              <img
                className="__media-content __media-content--image"
                src={catalogImage}
                alt={catalogImageAlternateText || ""}
              />
            </div>
          )}
          <div style={{ alignSelf: "center" }}>
            {childProductName && <div className="__childproductname">{childProductName}</div>}
            {productCode && (
              <Title defaultLevel={HEADING_LEVELS.FIVE} className="__productcode">
                {`#${productCode}`}
              </Title>
            )}
          </div>
          {ctaLink && (
            <a className="__ctaLink" tabIndex="0" href={ctaLink}>
              <div className="__arrow">
                <div className="__a-icon">&rsaquo;</div>
              </div>
            </a>
          )}
        </div>
        <div className="__tags __tags--bottom">
          {isAddBrand && <div className="__tag __tag--brand">{addYourBrandLabel}</div>}
        </div>
        <div
          tabIndex="0"
          style={{ height: 0, width: 0, position: "absolute" }}
          onFocus={focusOnContent}
        >
          &nbsp;
        </div>
      </div>
    </div>
  );
};

/**
 * Renders the content tile for a non-product hot spot (generic content)
 */
export const GenericHotSpotContent = ({
  tileInfo,
  style,
  onClose,
  onMouseEnter,
  onMouseLeave,
  onKeyUp,
  focusOnContent,
  isVideoClicked,
  onVideoClick,
  addYourBrandLabel,
  newProductLabel,
}) => {
  // Defensive check for missing data
  if (!tileInfo) {
    console.error('GenericHotSpotContent: tileInfo is undefined');
    return null;
  }

  const {
    title,
    body,
    subTitle,
    hasMedia,
    detailsLink,
    detailsLinkText,
    imageUrl,
    mediaAltText,
    videoUrl,
    videoThumbnailUrl,
    ctaLinkNode,
    halfStyle,
  } = tileInfo;

  const hasImageOrVideo = hasMedia && (imageUrl || videoUrl);
  let mainClassName = "__main";

  if (hasImageOrVideo) {
    mainClassName += " __main--withmedia";
  } else if (halfStyle && !ctaLinkNode) {
    mainClassName = "__main __main--5050";
  }

  const renderMedia = () => {
    if (!hasImageOrVideo) return null;

    if (imageUrl) {
      return (
        <div className="__media">
          <img
            alt={mediaAltText || ""}
            className="__media-content __media-content--image"
            src={imageUrl}
          />
        </div>
      );
    }

    if (videoUrl) {
      return (
        <div className="__media">
          {isVideoClicked ? (
            <VideoIFrame handleBlur={false} video={videoUrl} title="" />
          ) : (
            <button
              type="button"
              tabIndex="0"
              onKeyUp={(e) => {
                if (e.keyCode === 13) onVideoClick();
              }}
              onClick={onVideoClick}
              className="__video-button"
            >
              <div className="__playbtn">
                <img
                  className="__media-content __media-content--image __media-content--image-clickable"
                  alt={mediaAltText || ""}
                  src={videoThumbnailUrl}
                />
                <div className="___circle"></div>
                <div className="__triangle"></div>
              </div>
            </button>
          )}
        </div>
      );
    }

    return null;
  };

  const ctaUrl = ctaLinkNode || null;
  const showCta = !halfStyle || ctaLinkNode;
  const contentStyle = { ...style, width: "330px", height: "auto" };

  return (
    <div
      className="__hot-spot-content __hot-spot-content--active"
      tabIndex="0"
      id="hot-spot-content"
      style={contentStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyUp={onKeyUp}
    >
      <button className="__close darticon-close" onClick={onClose}></button>
      <div className="__content">
        <div className="__tags __tags--top">
          {/* Generic content doesn't show isNew tag */}
        </div>
        <div className={mainClassName}>
          {renderMedia()}
          <div style={{ alignSelf: "center" }}>
            {title && (
              <Title defaultLevel={HEADING_LEVELS.FIVE} className="__title">
                {title}
              </Title>
            )}
            {subTitle && (
              <Title defaultLevel={HEADING_LEVELS.SIX} className="__subtitle">
                {subTitle}
              </Title>
            )}
            {body && (
              <div className="__body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }} />
            )}
            {detailsLink && (
              <a className="__details" href={detailsLink}>
                {detailsLinkText}
              </a>
            )}
          </div>
          {showCta && ctaUrl && (
            <a className="__ctaLink" tabIndex="0" href={ctaUrl}>
              <div className="__arrow">
                <div className="__a-icon">&rsaquo;</div>
              </div>
            </a>
          )}
        </div>
        <div className="__tags __tags--bottom">
          {/* Add brand tag can be shown here if needed */}
        </div>
        <div
          tabIndex="0"
          style={{ height: 0, width: 0, position: "absolute" }}
          onFocus={focusOnContent}
        >
          &nbsp;
        </div>
      </div>
    </div>
  );
};

/**
 * Loading skeleton for hot spot content
 */
export const HotSpotLoadingContent = ({ style, onMouseEnter, onMouseLeave }) => {
  const loadingStyle = { ...style, width: "330px", height: "auto" };

  return (
    <div
      className="__hot-spot-content __hot-spot-content--active"
      style={loadingStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="__content __content--mock">
        <div className="__main __main--withmedia">
          <div className="__media __media--mock">
            <div></div>
          </div>
          <div style={{ alignSelf: "baseline" }}>
            <div className="__bar __bar--dark"></div>
            <div className="__bar __bar--thin"></div>
            <div className="__bar"></div>
          </div>
          <div className="__circle"></div>
        </div>
      </div>
    </div>
  );
};
